import React, { useState, useEffect, useRef } from 'react';
import { X, Download, Share } from 'lucide-react';

const DISMISS_KEY = 'mp-pwa-dismissed';
const DISMISS_KEY_IOS = 'mp-pwa-dismissed-ios';
const VISIT_KEY = 'mp-visit-count';
const COOKIE_CONSENT_NAME = 'slot_tvde_cookie_consent';

// A decisão de cookies foi tomada? (banner aceitou/rejeitou) — evita sobreposição.
const cookieDecisionMade = () =>
  document.cookie.split('; ').some((c) => c.startsWith(`${COOKIE_CONSENT_NAME}=`));

const dismissedRecently = (key) => {
  const ts = localStorage.getItem(key);
  if (!ts) return false;
  const days = (Date.now() - parseInt(ts, 10)) / (1000 * 60 * 60 * 24);
  return days <= 7;
};

const PWAInstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const deferredRef = useRef(null);
  const shownRef = useRef(false);

  useEffect(() => {
    const standalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    if (standalone) return; // já instalada

    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Contar visitas — só mostramos a partir da 2ª visita (nunca na 1ª)
    const visits = parseInt(localStorage.getItem(VISIT_KEY) || '0', 10) + 1;
    localStorage.setItem(VISIT_KEY, String(visits));

    const handleBeforeInstall = (e) => {
      e.preventDefault();
      deferredRef.current = e;
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Verifica periodicamente se já estão reunidas as condições para mostrar
    const canShow = () => {
      if (shownRef.current) return false;
      if (visits < 2) return false; // não na 1ª visita
      if (!cookieDecisionMade()) return false; // não sobrepor banner de cookies
      if (iOS) return !dismissedRecently(DISMISS_KEY_IOS);
      return !!deferredRef.current && !dismissedRecently(DISMISS_KEY);
    };

    const timer = setInterval(() => {
      if (canShow()) {
        shownRef.current = true;
        setShowPrompt(true);
        clearInterval(timer);
      }
    }, 1500);

    // Limite: parar de tentar ao fim de ~45s
    const stop = setTimeout(() => clearInterval(timer), 45000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      clearInterval(timer);
      clearTimeout(stop);
    };
  }, []);

  const handleInstall = async () => {
    const dp = deferredRef.current;
    if (!dp) return;
    dp.prompt();
    const { outcome } = await dp.userChoice;
    if (outcome === 'accepted') setShowPrompt(false);
    deferredRef.current = null;
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem(isIOS ? DISMISS_KEY_IOS : DISMISS_KEY, Date.now().toString());
  };

  if (!showPrompt) return null;

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-white rounded-xl shadow-2xl border border-gray-200 z-40 overflow-hidden animate-slide-up"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-green-500 to-green-600 p-4 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/icons/icon-192x192.png"
              alt="Marketplace TVDE"
              className="w-12 h-12 rounded-xl bg-white object-contain p-1"
            />
            <div>
              <h3 className="font-bold text-lg">Marketplace TVDE</h3>
              <p className="text-green-100 text-sm">Adicionar ao ecrã principal</p>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            data-testid="pwa-install-close"
            aria-label="Fechar"
            className="p-1 hover:bg-white/20 rounded-full transition"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {isIOS ? (
          <div>
            <p className="text-gray-700 mb-4">
              Adicione o Marketplace TVDE ao ecrã principal do seu iPhone/iPad para
              ver e publicar anúncios mais depressa:
            </p>
            <ol className="space-y-3 text-sm text-gray-600">
              <li className="flex items-start gap-2">
                <span className="bg-green-100 text-green-700 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 font-semibold">1</span>
                <span className="flex items-center gap-1">
                  Toque no botão <strong>Partilhar</strong> <Share className="h-4 w-4 inline" />
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-green-100 text-green-700 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 font-semibold">2</span>
                <span>Deslize e toque em <strong>"Adicionar ao ecrã principal"</strong></span>
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-green-100 text-green-700 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 font-semibold">3</span>
                <span>Toque em <strong>"Adicionar"</strong></span>
              </li>
            </ol>
            <button
              onClick={handleDismiss}
              data-testid="pwa-install-ios-ok"
              className="w-full mt-4 py-3 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition"
            >
              Entendi
            </button>
          </div>
        ) : (
          <div>
            <p className="text-gray-700 mb-4">
              Adicione o Marketplace TVDE ao ecrã principal para ver e publicar
              anúncios de viaturas e motoristas TVDE, e receber contactos mais depressa.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleDismiss}
                data-testid="pwa-install-later"
                className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition"
              >
                Agora não
              </button>
              <button
                onClick={handleInstall}
                data-testid="pwa-install-accept"
                className="flex-1 py-3 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 transition flex items-center justify-center gap-2"
              >
                <Download className="h-5 w-5" />
                Adicionar
              </button>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default PWAInstallPrompt;
